import InlineTextButton from '@ben/orion/inline-text-button';
import Input, { InputVariant } from '@ben/orion/input';
import type {
  IModularBenefitDetail,
  RecalculationConfig,
} from '@ben/shared/types/columbia';
import { memo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import styled, { css } from 'styled-components';
import TargetUserInputKeySelect from './target-user-input-key-select';

const PAIRED_FIELDS_ERROR =
  'Minimum formula and target user input key must both be set or both left blank';

const isNonEmptyFormula = (value: string | null | undefined) =>
  typeof value === 'string' && value.trim().length > 0;

const validatePairedFields = (
  formula: string | null | undefined,
  targetKey: number | null | undefined
) => {
  const hasFormula = isNonEmptyFormula(formula);
  const hasKey = targetKey != null;
  if (hasFormula === hasKey) {
    return true;
  }
  return PAIRED_FIELDS_ERROR;
};

interface PreRecalculationRuleProps {
  index: number;
  recalculationConfig: RecalculationConfig;
}

const PreRecalculationRule = ({
  index,
  recalculationConfig,
}: PreRecalculationRuleProps) => {
  const { control, setValue, trigger, getValues, clearErrors } =
    useFormContext<IModularBenefitDetail>();
  const [isRuleVisible, setIsRuleVisible] = useState(
    isNonEmptyFormula(recalculationConfig.preRecalculationMinFormula) ||
      recalculationConfig.targetUserInputKey != null
  );
  const formulaFieldName =
    `recalculationConfigs.${index}.preRecalculationMinFormula` as const;
  const keyFieldName =
    `recalculationConfigs.${index}.targetUserInputKey` as const;

  const removeRule = () => {
    setValue(formulaFieldName, null, { shouldDirty: true });
    setValue(keyFieldName, null, { shouldDirty: true });
    clearErrors([formulaFieldName, keyFieldName]);
    setIsRuleVisible(false);
  };

  if (!isRuleVisible) {
    return (
      <RuleActionWrapper>
        <StyledInlineTextButton
          label="Add rule"
          aria-label="Add pre-recalculation rule"
          onClick={() => setIsRuleVisible(true)}
        />
      </RuleActionWrapper>
    );
  }

  return (
    <FloorFieldsWrapper>
      <RuleActionWrapper>
        <StyledInlineTextButton
          label="Remove rule"
          aria-label="Remove pre-recalculation rule"
          onClick={removeRule}
        />
      </RuleActionWrapper>
      <Controller
        name={formulaFieldName}
        control={control}
        rules={{
          validate: (formula) =>
            validatePairedFields(formula, getValues(keyFieldName)),
        }}
        render={({ field, fieldState }) => (
          <Input
            {...field}
            value={field.value ?? ''}
            variant={InputVariant.TextArea}
            name="Pre-recalculation minimum formula"
            hintText="Optional floor evaluated before scheduled repricing. Raises the target user input when below the result; never lowers it. Leave blank with the key to skip."
            hasError={!!fieldState.error}
            errorMessage={fieldState.error?.message}
            width="100%"
            height="80px"
            onChange={(event) => {
              field.onChange(event.target.value || null);
              void trigger(keyFieldName);
            }}
            data-testid="pre-recalculation-min-formula"
          />
        )}
      />
      <TargetUserInputKeySelect
        name={keyFieldName}
        validate={(targetKey) =>
          validatePairedFields(getValues(formulaFieldName), targetKey)
        }
        onValueChange={() => {
          void trigger([formulaFieldName, keyFieldName]);
        }}
      />
    </FloorFieldsWrapper>
  );
};

export default memo(PreRecalculationRule);

const StyledInlineTextButton = styled(InlineTextButton)`
  ${({ theme }) => theme.typography.bodyLg2};
`;

const FloorFieldsWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    margin-bottom: ${theme.spacing(3)};
  `}
`;

const RuleActionWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;
